import { useState, useEffect } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BackupSection } from "@/components/BackupSection";
import { PushSection } from "@/components/PushSection";
import { GoogleCalendarSection } from "@/components/GoogleCalendarSection";
import {
  Bell,
  Mail,
  Send,
  AlertTriangle,
  Target,
  CreditCard,
  CalendarDays,
  Loader2,
  CheckCircle,
  Clock,
  Play,
  Zap
} from "lucide-react";

export const NotificationsPage = () => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState({
    email: "",
    late_orders: true,
    objectives_reached: true,
    payment_received: true,
    weekly_summary: true
  });
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingLate, setSendingLate] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, schedulerRes] = await Promise.all([
          api.getNotificationSettings(),
          isAdmin ? api.getSchedulerStatus().catch(() => ({ data: null })) : Promise.resolve({ data: null })
        ]);
        setSettings(settingsRes.data);
        setSchedulerStatus(schedulerRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin]);

  const handleSave = async () => {
    if (!settings.email) {
      toast.error("Veuillez saisir un email");
      return;
    }
    try {
      setSaving(true);
      await api.updateNotificationSettings(settings);
      toast.success("Paramètres sauvegardés");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setSendingTest(true);
      const res = await api.sendTestEmail();
      toast.success(res.data.message);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'envoi"));
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendLateOrders = async () => {
    try {
      setSendingLate(true);
      const res = await api.sendLateOrdersNotification();
      toast.success(res.data.message);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'envoi"));
    } finally {
      setSendingLate(false);
    }
  };

  const handleSendWeeklySummary = async () => {
    try {
      setSendingSummary(true);
      const res = await api.sendWeeklySummary();
      toast.success(res.data.message);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'envoi"));
    } finally {
      setSendingSummary(false);
    }
  };

  const handleTriggerJob = async () => {
    try {
      setTriggeringJob(true);
      const res = await api.triggerSchedulerNow();
      toast.success(res.data.message);
      // Refresh status after a few seconds
      setTimeout(async () => {
        try {
          const statusRes = await api.getSchedulerStatus();
          setSchedulerStatus(statusRes.data);
        } catch (_) {}
        setTriggeringJob(false);
      }, 3000);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
      setTriggeringJob(false);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleString("fr-FR", {
      weekday: "long", day: "numeric", month: "long",
      year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in" data-testid="notifications-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Notifications email</h1>
        <p className="text-muted-foreground mt-1">Configurez vos alertes par email</p>
      </div>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Paramètres
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="notif-email">Email de destination</Label>
            <div className="flex gap-3">
              <Input
                id="notif-email"
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="votre@email.com"
                className="flex-1"
                data-testid="notif-email-input"
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <p className="font-medium text-sm">Types de notifications</p>
            
            <div className="flex items-center justify-between p-3 rounded-sm border">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="font-medium text-sm">Commandes en retard</p>
                  <p className="text-xs text-muted-foreground">Alerte quand des commandes dépassent leur date estimée</p>
                </div>
              </div>
              <Switch
                checked={settings.late_orders}
                onCheckedChange={(v) => setSettings({ ...settings, late_orders: v })}
                data-testid="toggle-late-orders"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-sm border">
              <div className="flex items-center gap-3">
                <Target className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-sm">Objectifs atteints</p>
                  <p className="text-xs text-muted-foreground">Notification quand un objectif annuel est atteint</p>
                </div>
              </div>
              <Switch
                checked={settings.objectives_reached}
                onCheckedChange={(v) => setSettings({ ...settings, objectives_reached: v })}
                data-testid="toggle-objectives"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-sm border">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-sm">Paiement reçu</p>
                  <p className="text-xs text-muted-foreground">Alerte à chaque nouveau paiement enregistré</p>
                </div>
              </div>
              <Switch
                checked={settings.payment_received}
                onCheckedChange={(v) => setSettings({ ...settings, payment_received: v })}
                data-testid="toggle-payments"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-sm border">
              <div className="flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium text-sm">Résumé hebdomadaire</p>
                  <p className="text-xs text-muted-foreground">Récapitulatif des commandes de la semaine</p>
                </div>
              </div>
              <Switch
                checked={settings.weekly_summary}
                onCheckedChange={(v) => setSettings({ ...settings, weekly_summary: v })}
                data-testid="toggle-weekly"
              />
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto" data-testid="save-notif-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Sauvegarder les paramètres
          </Button>
        </CardContent>
      </Card>

      {/* Manual triggers (admin only) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Envoi manuel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Envoyez manuellement les notifications aux utilisateurs ayant activé les alertes.
              Requiert une clé API Resend configurée dans le backend.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={handleSendTest} disabled={sendingTest} data-testid="send-test-btn">
                {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Email de test
              </Button>
              <Button variant="outline" onClick={handleSendLateOrders} disabled={sendingLate} data-testid="send-late-btn">
                {sendingLate ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                Commandes en retard
              </Button>
              <Button variant="outline" onClick={handleSendWeeklySummary} disabled={sendingSummary} data-testid="send-summary-btn">
                {sendingSummary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarDays className="h-4 w-4 mr-2" />}
                Résumé hebdomadaire
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduler Status (admin only) */}
      {isAdmin && schedulerStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Scheduler automatique
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-sm bg-muted/50">
              <div className={`h-3 w-3 rounded-full ${schedulerStatus.running ? "bg-green-500" : "bg-red-500"}`} />
              <span className="font-medium text-sm">
                {schedulerStatus.running ? "Actif" : "Inactif"}
              </span>
              <span className="text-sm text-muted-foreground">
                - Envoi automatique chaque lundi à 9h
              </span>
            </div>

            {schedulerStatus.next_run && (
              <div className="text-sm">
                <span className="text-muted-foreground">Prochain envoi :</span>{" "}
                <span className="font-medium">{formatDateTime(schedulerStatus.next_run)}</span>
              </div>
            )}

            {schedulerStatus.last_run && (
              <div className="p-3 rounded-sm border space-y-1">
                <p className="text-sm font-medium">Dernier envoi automatique</p>
                <p className="text-xs text-muted-foreground">{formatDateTime(schedulerStatus.last_run.run_at)}</p>
                <div className="flex flex-wrap gap-4 mt-2 text-xs">
                  <span>{schedulerStatus.last_run.emails_sent} email(s) envoyé(s)</span>
                  <span>{schedulerStatus.last_run.late_orders_count} commande(s) en retard</span>
                  <span>{schedulerStatus.last_run.new_orders_count} nouvelle(s) commande(s)</span>
                </div>
              </div>
            )}

            <Button
              variant="outline"
              onClick={handleTriggerJob}
              disabled={triggeringJob}
              data-testid="trigger-scheduler-btn"
            >
              {triggeringJob ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Lancer maintenant
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Push notifications (all users) */}
      <PushSection />

      {/* Google Calendar (all users) */}
      <GoogleCalendarSection />

      {/* Backup section (all users) */}
      <BackupSection />
    </div>
  );
};

export default NotificationsPage;
